

import { BaseService } from '../../../http/BaseHttpService';
import {
    GetChatMessagesRequest,
    IChatMessagesService,
    SendChatMessageResponse,
    SendProGPTChatMessageRequest,
} from '../../../chat/interfaces/IChatMessagesService';

import { ProGPTResultDto } from '../dtos/responses/ProGPTResultDto';
import { ProGPTChatMessageDto } from '../dtos/responses/ProGPTChatMessageDto';
import { ProGPTSendMessageResponseDto } from '../dtos/responses/ProGPTSendMessageResponseDto';

import { mapProGPTChatMessageDtoToModel } from '../mappers/ProGPTChatMessageMapper';

import { buildProGPTUrl } from '../../../../features/utils/buildProGPTUrl';
import { ProGPTDownloadChatMessageRequest } from '../dtos/requests/ProGPTDownloadChatMessageRequest';
import { ProGPTCreateFeedbackResponse } from '../dtos/responses/ProGPTCreateFeedbackResponse';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';
import { AuthorRoles, ChatMessage } from '../../../../domain/messages/models/ChatMessage';


export class ProGPTChatMessagesService extends BaseService implements IChatMessagesService {
    public readonly providerType = ChatProviderType.ProGPT;

    constructor(private readonly baseUrl: string) {
        super();
    }

    public async getChatMessagesAsync(request: GetChatMessagesRequest, accessToken: string): Promise<ChatMessage[]> {
        const searchParams = new URLSearchParams();

        if (request.skip !== undefined) {
            searchParams.set('skip', request.skip.toString());
        }

        if (request.count !== undefined) {
            searchParams.set('count', request.count.toString());
        }

        const queryString = searchParams.toString();
        const url = buildProGPTUrl(
            this.baseUrl,
            `ChatSession/${request.chatId}/messages${queryString ? `?${queryString}` : ''}`,
        );

        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTChatMessageDto[]>>(
            {
                url,
                method: 'GET',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to load ProGPT chat messages.');
        }

        return response.value.map(mapProGPTChatMessageDtoToModel);
    }

    public async downloadChatMessageAsync(
        request: ProGPTDownloadChatMessageRequest,
        accessToken: string,
        signal?: AbortSignal,
    ): Promise<File> {
        const response = await this.getBlobResponseAsync<Blob>(
            {
                url: buildProGPTUrl(this.baseUrl, 'ChatSession/downloadChatMessage'),
                method: 'POST',
                body: {
                    htmlText: request.htmlText,
                    fileName: request.fileName,
                },
            },
            accessToken,
            signal,
        );

        return new File([response], request.fileName, { type: response.type || 'application/octet-stream' });
    }

    public async createChatFeedbackAsync(
        messageId: string,
        chatId: string,
        userFeedback: string,
        accessToken: string,
        signal?: AbortSignal,
    ): Promise<ProGPTCreateFeedbackResponse> {
        await this.getResponseAsync<Record<string, never>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${messageId}/feedback`),
                method: 'POST',
                body: {
                    messageId,
                    chatId,
                    userFeedback,
                },
            },
            accessToken,
            signal,
        );

        return { status: 'created' };
    }

    public async sendMessageAsync(
        request: SendProGPTChatMessageRequest,
        accessToken: string,
        signal?: AbortSignal,
    ): Promise<SendChatMessageResponse> {
        const formData = new FormData();
        formData.append('Input', request.input);
        formData.append('ChatId', request.chatId);

        formData.append('ThinkLonger', (request.thinkLonger ?? false).toString());


        if (request.selectedAgents && request.selectedAgents.length > 0) {
            for (const selectedAgent of request.selectedAgents) {
                formData.append('SelectedAgentIds', selectedAgent);
            }
        }

        if (request.images && request.images.length > 0) {
            for (const image of request.images) {
                formData.append('ImageFiles', image);
            }
        }

        if (request.regenerate !== undefined) {
            formData.append('Regenerate', request.regenerate.toString());
        }

        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTSendMessageResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${request.chatId}/messages`),
                method: 'POST',
                body: formData,
            },
            accessToken,
            signal,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to send ProGPT chat message.');
        }
        return {
            status: 'streaming',
            chatId: request.chatId,
            id: response.value.message.id,
            message: response.value.message.content,
            citations: response.value.message.citations,
            agentSources: response.value.message.agentSources,
            tokens: response.value.message.tokenUsage
        };
    }

    public async savePartialMessageAsync(chatId: string, content: string, accessToken: string, role: AuthorRoles = AuthorRoles.Bot): Promise<void> {
        const url = buildProGPTUrl(this.baseUrl, `ChatSession/${chatId}/saveMessage`);

        await this.getResponseAsync(
            {
                url,
                method: 'POST',
                body: { Content: content, Role: role }
            },
            accessToken,
        );
    }
}
